<script setup>
import { computed, inject, onBeforeUnmount, reactive, watch, toRef } from 'vue';
import kebabCase from 'lodash/kebabCase';

const props = defineProps({
	title: {
		type: String,
		required: true
	},
	id: {
		type: String,
		required: false
	},
	icon: {
		type: String,
		required: false
	},
	// this prop is use in the parent
	activeClass: {
		type: String,
		required: false
	},
	to: {
		type: [String, Object],
		required: false
	},
	disabled: {
		type: Boolean,
		default: false
	},
	hidden: {
		type: Boolean,
		default: false
	},
	class: {
		type: String,
	}
});

const emit = defineEmits(['open', 'close']);

const tabsContext = inject('tabsContext', null);

// Cross-link tabs are identified by their target route. Two tabs can point at
// the same route name and differ only by query (e.g. `?tab=`), so the query
// values take part in the id to keep it unique.
const computedId = computed(() => {
	if (!props.to) {
		return kebabCase(props.id || props.title);
	}

	if (typeof props.to === 'string') {
		return kebabCase(props.to);
	}

	const query = props.to.query ? Object.values(props.to.query) : [];

	return kebabCase([props.to.name, ...query].join(' '));
});

const isOpen = computed(() => tabsContext?.active === computedId.value);

watch(isOpen, (opened) => {
	opened ? emit('open') : emit('close');
});

// Register a reactive descriptor with the parent instead of instance.proxy
const tabData = reactive({
	computedId,
	isOpen,
	title: toRef(props, 'title'),
	icon: toRef(props, 'icon'),
	activeClass: toRef(props, 'activeClass'),
	to: toRef(props, 'to'),
	disabled: toRef(props, 'disabled'),
	hidden: toRef(props, 'hidden'),
	class: toRef(props, 'class'),
});

if (tabsContext) {
	tabsContext.tabs.push(tabData);
}

onBeforeUnmount(() => {
	if (tabsContext) {
		const index = tabsContext.tabs.indexOf(tabData);
		if (index > -1) {
			tabsContext.tabs.splice(index, 1);
		}
	}
});
</script>

<template>
    <div v-show="isOpen" class="tab">
        <slot :to="to" :id="id" :title="title" />
    </div>
</template>